// CRM-NANCOZI — Mon Profil (accessible à tous les utilisateurs)
import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import api from '../services/api';
import { updateUser } from '../store/slices/authSlice';
import {
  User, Mail, Phone, Briefcase, Shield, Save, KeyRound, Eye, EyeOff,
} from 'lucide-react';

function Field({ label, error, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>
        {label}
      </label>
      {children}
      {error && <p style={{ color: '#C00000', fontSize: 11, margin: '3px 0 0' }}>
        {Array.isArray(error) ? error[0] : error}
      </p>}
    </div>
  );
}

const BADGE_ROLE = { administrateur: { label: 'Administrateur', color: '#1F4E79', bg: '#EBF3FB' }, commercial: { label: 'Commercial', color: '#1E7145', bg: '#E8F5E9' } };
const BADGE_STATUT = { actif: { label: 'Actif', color: '#1E7145', bg: '#E8F5E9' }, suspendu: { label: 'Suspendu', color: '#C00000', bg: '#FCE4EC' }, inactif: { label: 'Inactif', color: '#6B7280', bg: '#F3F4F6' } };

export default function ProfilPage() {
  const { user } = useSelector(s => s.auth);
  const dispatch = useDispatch();

  const [saving,  setSaving]  = useState(false);
  const [savingP, setSavingP] = useState(false);
  const [showPw,  setShowPw]  = useState(false);
  const [errors,  setErrors]  = useState({});

  const [form, setForm] = useState({
    nom_util:    user?.nom_util    || '',
    prenom_util: user?.prenom_util || '',
    email:       user?.email       || '',
    telephone:   user?.telephone   || '',
    poste:       user?.poste       || '',
  });
  const [mdp, setMdp] = useState({ nouveau: '', confirmer: '' });

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErrors(e => ({ ...e, [k]: '' })); };

  const handleSaveProfil = async (e) => {
    e.preventDefault();
    setErrors({});
    setSaving(true);
    try {
      await api.patch(`/utilisateurs/${user.id}/`, {
        nom_util: form.nom_util, prenom_util: form.prenom_util,
        email: form.email, telephone: form.telephone, poste: form.poste,
      });
      dispatch(updateUser({ nom_util: form.nom_util, prenom_util: form.prenom_util, email: form.email, telephone: form.telephone, poste: form.poste }));
      toast.success('Profil mis à jour avec succès.');
    } catch (err) {
      const d = err.response?.data;
      if (d && typeof d === 'object') setErrors(d);
      else toast.error('Erreur lors de la mise à jour.');
    } finally { setSaving(false); }
  };

  const handleChangeMdp = async (e) => {
    e.preventDefault();
    if (mdp.nouveau.length < 8) { setErrors({ mdp_nouveau: 'Minimum 8 caractères.' }); return; }
    if (mdp.nouveau !== mdp.confirmer) { setErrors({ mdp_confirmer: 'Les mots de passe ne correspondent pas.' }); return; }
    setSavingP(true);
    try {
      await api.patch(`/utilisateurs/${user.id}/`, { password: mdp.nouveau, confirm_password: mdp.confirmer });
      toast.success('Mot de passe modifié avec succès.');
      setMdp({ nouveau: '', confirmer: '' });
    } catch (err) {
      const d = err.response?.data;
      if (d?.password) setErrors({ mdp_nouveau: d.password });
      else toast.error('Erreur lors du changement de mot de passe.');
    } finally { setSavingP(false); }
  };

  const roleInfo   = BADGE_ROLE[user?.role]   || { label: user?.role,   color: '#6B7280', bg: '#F3F4F6' };
  const statutInfo = BADGE_STATUT[user?.statut] || { label: user?.statut, color: '#6B7280', bg: '#F3F4F6' };
  const initiales  = `${user?.prenom_util?.[0] || ''}${user?.nom_util?.[0] || ''}`.toUpperCase();

  return (
    <div style={{ maxWidth: 720 }}>

      {/* En-tête profil */}
      <div className="card" style={{ marginBottom: 20, padding: '20px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: '#1F4E79', color: 'white',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, fontSize: 22, flexShrink: 0,
          }}>
            {initiales}
          </div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#1F2937', marginBottom: 4 }}>
              {user?.prenom_util} {user?.nom_util}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 99,
                color: roleInfo.color, background: roleInfo.bg }}>
                {roleInfo.label}
              </span>
              <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 99,
                color: statutInfo.color, background: statutInfo.bg }}>
                {statutInfo.label}
              </span>
              {user?.poste && (
                <span style={{ fontSize: 11, color: '#6B7280', padding: '3px 10px', border: '1px solid #E5E7EB', borderRadius: 99 }}>
                  {user.poste}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Informations personnelles */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ padding: '16px 20px 0', borderBottom: '1px solid #F3F4F6', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <User size={15} color="#1F4E79" />
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#1F2937' }}>
              Informations personnelles
            </h3>
          </div>
        </div>
        <form onSubmit={handleSaveProfil} style={{ padding: '0 20px 20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="Prénom *" error={errors.prenom_util}>
              <div style={{ position: 'relative' }}>
                <User size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
                <input className="form-input" value={form.prenom_util}
                  onChange={e => set('prenom_util', e.target.value)}
                  style={{ paddingLeft: 32 }} required />
              </div>
            </Field>
            <Field label="Nom *" error={errors.nom_util}>
              <div style={{ position: 'relative' }}>
                <User size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
                <input className="form-input" value={form.nom_util}
                  onChange={e => set('nom_util', e.target.value)}
                  style={{ paddingLeft: 32 }} required />
              </div>
            </Field>
          </div>

          <Field label="Adresse email *" error={errors.email}>
            <div style={{ position: 'relative' }}>
              <Mail size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
              <input className="form-input" type="email" value={form.email}
                onChange={e => set('email', e.target.value)}
                style={{ paddingLeft: 32 }} required />
            </div>
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="Téléphone" error={errors.telephone}>
              <div style={{ position: 'relative' }}>
                <Phone size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
                <input className="form-input" value={form.telephone}
                  onChange={e => set('telephone', e.target.value)}
                  placeholder="+225 07 00 00 00 00"
                  style={{ paddingLeft: 32 }} />
              </div>
            </Field>
            <Field label="Poste / Fonction" error={errors.poste}>
              <div style={{ position: 'relative' }}>
                <Briefcase size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
                <input className="form-input" value={form.poste}
                  onChange={e => set('poste', e.target.value)}
                  placeholder="Ex : Commercial senior"
                  style={{ paddingLeft: 32 }} />
              </div>
            </Field>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
            <button type="submit" disabled={saving}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 20px',
                fontSize: 13, fontWeight: 600, border: 'none', borderRadius: 8,
                background: '#1F4E79', color: 'white', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
              <Save size={14} /> {saving ? 'Enregistrement…' : 'Sauvegarder le profil'}
            </button>
          </div>
        </form>
      </div>

      {/* Sécurité — Changement de mot de passe */}
      <div className="card">
        <div style={{ padding: '16px 20px 0', borderBottom: '1px solid #F3F4F6', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Shield size={15} color="#C00000" />
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#1F2937' }}>
              Sécurité — Changer le mot de passe
            </h3>
          </div>
        </div>
        <form onSubmit={handleChangeMdp} style={{ padding: '0 20px 20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="Nouveau mot de passe *" error={errors.mdp_nouveau}>
              <div style={{ position: 'relative' }}>
                <KeyRound size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
                <input className="form-input" type={showPw ? 'text' : 'password'}
                  value={mdp.nouveau} onChange={e => setMdp(m => ({ ...m, nouveau: e.target.value }))}
                  placeholder="Min. 8 caractères" style={{ paddingLeft: 32, paddingRight: 38 }} />
                <button type="button" onClick={() => setShowPw(v => !v)}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280' }}>
                  {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </Field>
            <Field label="Confirmer le mot de passe *" error={errors.mdp_confirmer}>
              <div style={{ position: 'relative' }}>
                <KeyRound size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
                <input className="form-input" type={showPw ? 'text' : 'password'}
                  value={mdp.confirmer} onChange={e => setMdp(m => ({ ...m, confirmer: e.target.value }))}
                  placeholder="Répéter le mot de passe" style={{ paddingLeft: 32 }} />
              </div>
            </Field>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
            <button type="submit" disabled={savingP || !mdp.nouveau}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 20px',
                fontSize: 13, fontWeight: 600, border: 'none', borderRadius: 8,
                background: '#C00000', color: 'white',
                cursor: (savingP || !mdp.nouveau) ? 'not-allowed' : 'pointer',
                opacity: (savingP || !mdp.nouveau) ? 0.6 : 1 }}>
              <Shield size={14} /> {savingP ? 'Modification…' : 'Modifier le mot de passe'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
